import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { Link } from "react-aria-components";
import { cn } from "../../../lib";
import {
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  GlobeAmericasIcon,
  MapPinIcon,
  PhoneIcon,
} from "@heroicons/react/20/solid";

const titleWithContactInfoVariants = cva("title-with-contact", {
  variants: {
    color: {
      default: "text-black",
      light: "text-white",
    },
  },
  defaultVariants: {
    color: "default",
  },
});

interface TitleWithContactInfoProps
  extends VariantProps<typeof titleWithContactInfoVariants> {
  contactInfo: {
    title?: string;
    address?: {
      street: string;
      city: string;
      state: string;
      zip: string;
      country: string;
    };
    phoneNumber?: string;
    mobilePhoneNumber?: string;
    email?: string;
    website?: string;
  };
  className?: string;
}

const TitleWithContactInfo = (props: TitleWithContactInfoProps) => {
  const {
    color = "default",
    contactInfo: {
      title,
      address,
      phoneNumber,
      mobilePhoneNumber,
      email,
      website,
    },
    className,
  } = props;

  const addressLines = createAddressLines(address);
  return (
    <div className={cn(titleWithContactInfoVariants({ color }), className)}>
      <div className="flex flex-col py-2">
        {title && <span className="block font-extrabold py-2">{title}</span>}

        {addressLines.length > 0 && (
          <div className="mt-2 flex items-start space-x-3">
            <dt className="mt-0">
              <span className="sr-only">Email</span>
              <MapPinIcon
                className="h-5 w-5 text-lf-blue-light"
                aria-hidden="true"
              />
            </dt>
            <dd>
              {addressLines.map((line, index) => (
                <span key={index}>
                  {line}
                  {index < addressLines.length - 1 && <br />}
                </span>
              ))}
            </dd>
          </div>
        )}
        {phoneNumber && (
          <div className="mt-2 flex items-start space-x-3">
            <dt className="mt-0">
              <span className="sr-only">Phone Number</span>
              <PhoneIcon
                className="h-5 w-5 text-lf-blue-light"
                aria-hidden="true"
              />
            </dt>
            <dd>{phoneNumber}</dd>
          </div>
        )}
        {mobilePhoneNumber && (
          <div className="mt-2 flex items-start space-x-3">
            <dt className="mt-0">
              <span className="sr-only">Mobile</span>
              <DevicePhoneMobileIcon
                className="h-5 w-5 text-lf-blue-light"
                aria-hidden="true"
              />
            </dt>
            <dd>{mobilePhoneNumber}</dd>
          </div>
        )}
        {email && (
          <div className="mt-2 flex items-start space-x-3">
            <dt className="mt-0">
              <span className="sr-only">Email</span>
              <EnvelopeIcon
                className="h-5 w-5 text-lf-blue-light"
                aria-hidden="true"
              />
            </dt>
            <dd>
              <Link
                href={"mailto:" + email}
                target="_blank"
                className="hover:underline"
              >
                {email}
              </Link>
            </dd>
          </div>
        )}
        {website && (
          <div className="mt-2 flex items-start space-x-3">
            <dt className="mt-0">
              <span className="sr-only">Website</span>
              <GlobeAmericasIcon
                className="h-5 w-5 text-lf-blue-light"
                aria-hidden="true"
              />
            </dt>
            <dd>
              <Link
                href={normalizeWebsiteLink(website)}
                target="_blank"
                className="hover:underline"
              >
                Website
              </Link>
            </dd>
          </div>
        )}
      </div>
    </div>
  );
};

function createAddressLines(
  address: TitleWithContactInfoProps["contactInfo"]["address"],
): (string | null)[] {
  if (!address) {
    return [];
  }
  const { street, city, state, zip, country } = address || {};
  return [
    street || null,
    `${city || ""}, ${state || ""} ${zip || ""}`,
    `${country || ""}`,
  ];
}

function normalizeWebsiteLink(link: string) {
  if (link.startsWith("http://") || link.startsWith("https://")) {
    return link;
  }
  return "https://" + link;
}

TitleWithContactInfo.displayName = "TitleWithContactInfo";

export default TitleWithContactInfo;
export { titleWithContactInfoVariants };
export type { TitleWithContactInfoProps };
